import React, { useEffect, useMemo, useState, useCallback, useContext } from "react";
import { 
    format, parseISO, isBefore, subDays,
    startOfWeek, setDay, addWeeks
} from "date-fns";
import { Container, Card, Button, Form, Row, Col, Alert, Badge, Spinner, Modal, Accordion, InputGroup, FormCheck } from 'react-bootstrap';
import { fetchAllRequests, updateRequestStatus, fetchAllUsers, updateEVUserInfo } from 'services/charging-space-booking/chargingSpaceBookingService';
import { getItemFromStore } from 'helpers/utils';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import PageHeader from 'components/common/PageHeader';
import AppContext from 'context/Context';

const SITES = [
    { id: "EMPLAS_1", name: "EMPLAS 1" },
    { id: "EMPLAS_2", name: "EMPLAS 2" },
];

const SLOT_TYPES = {
    AM: "Half-day (Morning)",
    PM: "Half-day (Afternoon)",
    FULL: "Full day",
};

function renderStatusBadge(status) {
    let variant = 'secondary';
    let text = status;
    if (status === 'PENDING') {
        variant = 'warning';
        text = 'Pending Review';
    } else if (status === 'APPROVED') {
        variant = 'success';
        text = 'Approved';
    } else if (status === 'REJECTED') {
        variant = 'danger';
        text = 'Rejected';
    } else if (status === 'CANCELLED') {
        variant = 'secondary';
        text = 'Cancelled (User)';
    } else if (status === 'RELEASED') {
        variant = 'info';
        text = 'Released';
    }
    const icon = 
        status === 'PENDING' ? 'bi-hourglass-split' :
        status === 'APPROVED' ? 'bi-check-circle-fill' :
        status === 'REJECTED' ? 'bi-x-octagon-fill' :
        status === 'RELEASED' ? 'bi-patch-check-fill' :
        'bi-dash-circle-fill';

    return (
        <Badge bg={variant} className="ms-2 d-flex align-items-center">
            <i className={`bi ${icon} me-1`}></i> {text}
        </Badge>
    );
}

const ConfirmModal = ({ show, handleClose, handleConfirm, title, body, confirmText = 'Confirm' }) => (
    <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton className="border-bottom">
            <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-4">
            <div className="d-flex align-items-center">
                <i className="bi bi-exclamation-triangle-fill text-warning me-3" style={{ fontSize: '1.5rem' }}></i>
                <div>{body}</div>
            </div>
        </Modal.Body>
        <Modal.Footer className="border-0">
            <Button variant="outline-secondary" onClick={handleClose}>
                Close
            </Button>
            <Button variant="info" onClick={handleConfirm}>
                {confirmText}
            </Button>
        </Modal.Footer>
    </Modal>
);

ConfirmModal.propTypes = {
    show: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    handleConfirm: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    confirmText: PropTypes.string,
};

function UserSettingsModal({ show, handleClose, allUsers, refreshUserList }) {
    const [selectedUserId, setSelectedUserId] = useState(''); 
    const [site, setSite] = useState(SITES[0].id);
    const [plateNo, setPlateNo] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const selectedUser = useMemo(() => {
        const user = allUsers.find(u => u.userId == selectedUserId);
        return user;
    }, [allUsers, selectedUserId]);

    const hasChanges = useMemo(() => {
        if (!selectedUser) return false;

        const initialSite = selectedUser.site || SITES[0].id;
        const initialPlateNo = selectedUser.plateNo || '';
        const initialIsActive = selectedUser.isActive === undefined ? true : selectedUser.isActive;

        const siteChanged = site !== initialSite;
        const plateNoChanged = plateNo !== initialPlateNo;
        const isActiveChanged = isActive !== initialIsActive;

        return siteChanged || plateNoChanged || isActiveChanged;
    }, [selectedUser, site, plateNo, isActive]);
    
    const handleUserSelect = (newUserId) => {
        setSelectedUserId(newUserId); 

        const user = allUsers.find(u => u.userId == newUserId);
        if (user) {
            const currentSite = user.site || SITES[0].id;
            const currentPlateNo = user.plateNo || '';
            const currentIsActive = user.isActive === undefined ? true : user.isActive;
            setSite(currentSite);
            setPlateNo(currentPlateNo);
            setIsActive(currentIsActive);
        } else {
            setSite(SITES[0].id);
            setPlateNo('');
            setIsActive(true);
        }
    };

    const handleSave = async () => {
        if (!selectedUser) {
            toast.error("Please select a user.");
            return;
        }

        if (!hasChanges) {
             toast.info("No changes detected to save.");
             return;
        }

        setIsSaving(true);
        try {
            await updateEVUserInfo(selectedUser.userId, selectedUser.userName, site, plateNo, isActive);
            toast.success(`Updated for ${selectedUser.userName}.`);
            
            setSelectedUserId(''); 
            handleClose();

            refreshUserList();
        } catch (error) {
            toast.error("Error while saving to EVA server: " + error.message);
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        if (!show) {
            setSelectedUserId('');
            setSite(SITES[0].id);
            setPlateNo('');
            setIsActive(true);
        }
    }, [show]);

    return (
        <Modal show={show} onHide={handleClose} centered size="md">
            <Modal.Header closeButton>
                <Modal.Title className="fw-bold">
                    <i className="bi bi-person-gear me-2"></i> EV User Settings
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-4 p-3 border rounded bg-light">
                        <Form.Label className="fw-bold text-dark">1. Select User</Form.Label>
                        <Form.Select 
                            onChange={(e) => handleUserSelect(e.target.value)} 
                            defaultValue=""
                            size="lg"
                        >
                            <option value="" disabled>-- Select an user --</option>
                            {allUsers.map(user => (
                                <option key={user.userId} value={user.userId}>
                                    {user.userName}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    {selectedUser && (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">2. Site</Form.Label>
                                <Form.Select 
                                    value={site} 
                                    onChange={(e) => setSite(e.target.value)}
                                >
                                    {SITES.map((s) => (
                                        <option key={s.id} value={s.id}>
                                            {s.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">3. Plate No.</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    placeholder="Enter Plate No." 
                                    value={plateNo}
                                    onChange={(e) => setPlateNo(e.target.value)}
                                />
                                {/* <Form.Text className="text-muted fst-italic">
                                    Current Plate No.: <span className="fw-bold text-primary">{selectedUser.plateNo || "None"}</span>
                                </Form.Text> */}
                            </Form.Group>

                            <Form.Group className="mb-4 pt-3 border-top">
                                <Form.Label className="fw-bold">4. Status</Form.Label>
                                <FormCheck
                                    type="switch"
                                    id="user-status-switch"
                                    label={isActive ? "Active (Allow Booking)" : "Inactive (Block Booking)"}
                                    checked={isActive}
                                    onChange={(e) => setIsActive(e.target.checked)}
                                    className={`fw-medium ${isActive ? 'text-success' : 'text-danger'}`}
                                />
                            </Form.Group>

                            <div className="d-flex justify-content-end mt-4 pt-3 border-top">
                                <Button 
                                    variant="primary" 
                                    onClick={handleSave} 
                                    disabled={isSaving || !hasChanges}
                                >
                                    {isSaving ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" /> : <i className="bi bi-save me-2"></i>}
                                    Save
                                </Button>
                            </div>
                        </>
                    )}
                </Form>
            </Modal.Body>
        </Modal>
    );
}

UserSettingsModal.propTypes = {
    show: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    allUsers: PropTypes.arrayOf(PropTypes.shape({
        userId: PropTypes.number.isRequired,
        userName: PropTypes.string.isRequired,
        site: PropTypes.string,
        plateNo: PropTypes.string,
        isActive: PropTypes.bool,
    })).isRequired,
    refreshUserList: PropTypes.func.isRequired,
};

// ------------------- Main Component -------------------
export default function ChargingSpaceAdminApproval() {
    const [storeUser, setStoreUser] = useState({});
    useEffect(() => {
       const interval = setInterval(() => {
           const user = getItemFromStore('user', {}, sessionStorage);
           if (user?.userName) {
           setStoreUser(user);
           clearInterval(interval);
           }
       }, 100);
       return () => clearInterval(interval);
    }, []);

    const [activeKey, setActiveKey] = useState(null);
    const {
        config: { isFluid, isNavbarVerticalCollapsed },
        setConfig
    } = useContext(AppContext);
    const [kanbanIsFluid] = useState(isFluid);
    const [kanbanIsNavbarVerticalCollapsed] = useState(isNavbarVerticalCollapsed);

    useEffect(() => {
        setConfig('isFluid', true);
        setConfig('isNavbarVerticalCollapsed', true);

        return () => {
        setConfig('isFluid', kanbanIsFluid);
        setConfig('isNavbarVerticalCollapsed', kanbanIsNavbarVerticalCollapsed);
        };
    }, []);

    const today = useMemo(() => subDays(new Date().setHours(0, 0, 0, 0), 0), []); 
    const now = useMemo(() => new Date(), []);
    const defaultStartDate = useMemo(() => startOfWeek(now, { weekStartsOn: 1 }), [now]);
    const fridayOfCurrentWeek = useMemo(() => setDay(now, 5, { weekStartsOn: 1 }), [now]);
    const defaultEndDate = useMemo(() => addWeeks(fridayOfCurrentWeek, 1), [fridayOfCurrentWeek]);
    const defaultStartStr = useMemo(() => format(defaultStartDate, 'yyyy-MM-dd'), [defaultStartDate]);
    const defaultEndStr = useMemo(() => format(defaultEndDate, 'yyyy-MM-dd'), [defaultEndDate]);
    
    const [startDate, setStartDate] = useState(defaultStartStr);
    const [endDate, setEndDate] = useState(defaultEndStr);
    const [allRequests, setAllRequests] = useState([]);
    const [loadingRequests, setLoadingRequests] = useState(false);
    const [allUsers, setAllUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [filterUser, setFilterUser] = useState("all");
    const [filterSite, setFilterSite] = useState("all");
    
    const [showSettingsModal, setShowSettingsModal] = useState(false);

    const [showReleaseModalAdmin, setShowReleaseModalAdmin] = useState(false);
    const [releaseRequestIdAdmin, setReleaseRequestIdAdmin] = useState(null);

    const refreshRequestList = () => {
        loadRequests(startDate, endDate);
    };

    const loadRequests = useCallback(async (fromStr, toStr) => {
        const fromDate = parseISO(fromStr);
        const toDate = parseISO(toStr);
        
        if (toDate < fromDate) {
            toast.error("End date should be greater than Start date.");
            return;
        }

        setLoadingRequests(true);
        try {
            const data = await fetchAllRequests(fromDate, toDate); 
            setAllRequests(data);
        } catch (err) {
            toast.error("Error while loading requests: " + err.message);
            setAllRequests([]);
        } finally {
            setLoadingRequests(false);
        }
    }, []);

    useEffect(() => {
        loadRequests(startDate, endDate);
    }, []); 

    const loadUsers = useCallback(async () => {
        setLoadingUsers(true);
        try {
            const data = await fetchAllUsers();
            setAllUsers(data);
        } catch (err) {
            toast.error("Error while getting users: " + err.message);
            setAllUsers([]);
        } finally {
            setLoadingUsers(false);
        }
    }, []);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    const handleDateSearch = () => {
        loadRequests(startDate, endDate);
    };

    const nonCancelledRequests = useMemo(() => {
        return allRequests.filter(req => req.status !== 'CANCELLED');
    }, [allRequests]);

    const groupedRequests = useMemo(() => {
        const groups = {};
        
        let filtered = nonCancelledRequests.filter(req => 
            filterUser === 'all' || req.requestedBy === filterUser
        );

        filtered = filtered.filter(req => 
            filterSite === 'all' || req.site === filterSite
        );

        filtered.forEach(req => {
            if (!groups[req.requestedBy]) {
                groups[req.requestedBy] = [];
            }
            groups[req.requestedBy].push(req);
        });
        
        for (const userId in groups) {
            groups[userId].sort((a, b) => a.bookingDate - b.bookingDate);
        }

        return groups;
    }, [nonCancelledRequests, filterUser, filterSite]);

    const filteredRequestCount = useMemo(() => {
        return Object.values(groupedRequests).reduce((total, requests) => total + requests.length, 0);
    }, [groupedRequests]);

    const handleOpenReleaseModalAdmin = (requestId) => {
        setReleaseRequestIdAdmin(requestId);
        setShowReleaseModalAdmin(true);
    };

    const handleReleaseConfirmAdmin = async () => {
        setShowReleaseModalAdmin(false);
        setLoadingRequests(true); 

        try {
            const response = await updateRequestStatus(
                releaseRequestIdAdmin, 
                'RELEASED', 
                storeUser.userName
            );
            toast.success(response.message || "Released.");
            refreshRequestList(); 
        } catch (err) {
            toast.error(err.message || "Release failed.");
        } finally {
            setLoadingRequests(false); 
        }
    };

    const handleStatusUpdate = async (requestId, status, processedBy) => {
        const actionName = status === 'APPROVED' ? 'Approve' : 'Reject';
        
        setLoadingRequests(true); 
        try {
            await updateRequestStatus(requestId, status, processedBy);
            toast.success(`${actionName} the request successfully.`);
            loadRequests(startDate, endDate);
        } catch (err) {
            toast.error(`Error ${actionName}: ` + err.message);
        } finally {
            setLoadingRequests(false);
        }
    };
    
    const getUserName = (userId) => {
        const user = allUsers.find(u => u.userId === userId);
        return user ? user.userName : userId;
    };

    const getPlateNo = (userName) => {
        const user = allUsers.find(u => u.userName === userName);
        console.log(user)
        return  user?.plateNo ?? '';
    };

    const requestingUsersForFilter = useMemo(() => {
        return allUsers; 
    }, [allUsers]);

    const RequestItem = ({ request }) => {
        const isPending = request.status === 'PENDING';
        const bookingDate = parseISO(format(request.bookingDate, 'yyyy-MM-dd'));
        const isApprovedAndFuture = 
            request.status === 'APPROVED' 
            && !isBefore(bookingDate, today);
        console.log(request)
        let bgColor = 'bg-light';
        if (isPending) bgColor = 'bg-warning-subtle border-warning';
        else if (request.status === 'APPROVED') bgColor = 'bg-success-subtle border-success';
        else if (request.status === 'REJECTED') bgColor = 'bg-danger-subtle border-danger';

        return (
            <div className={`border rounded p-3 mb-2 d-flex flex-column flex-md-row justify-content-between align-items-md-start ${bgColor}`}>
                <div className="flex-grow-1 mb-2 mb-md-0">
                    <div className="fw-bold text-primary mb-1 d-flex align-items-center">
                        {renderStatusBadge(request.status)} 
                        <span className="ms-2 d-flex align-items-center"> 
                            <i className="bi bi-calendar-check me-2"></i>
                            {format(request.bookingDate, 'EEE d MMM yyyy')} | Site: {request.site.replace("_", " ")}
                        </span>
                    </div>
                    <div className="small text-muted mb-1">
                        <i className="bi bi-clock me-1"></i>Slot: <span className="fw-medium">{SLOT_TYPES[request.slotType]}</span> 
                        <span className="mx-2">|</span>
                        <i className="bi bi-car-fill me-1"></i>Requested by: <span className="fw-medium text-dark">{request.requestedBy}</span>
                         <span className="mx-2">|</span>
                           <i className="bi bi-car-fill me-1"></i>Plate No: <span className="fw-medium text-dark">{request.plateNo}</span>
                         <span className="mx-2">|</span>
                        <span className="text-muted">
                            {request.bayNo && <Badge bg={'success'}>{ request.bayNo}</Badge>} 
                        </span>
                    </div>
                    <div className="small text-secondary mt-1">
                        <i className="bi bi-info-circle me-1"></i> Requested at: {format(request.created, 'HH:mm:ss d MMM')}
                    </div>
                </div>
                <div className="text-md-end pt-2 pt-md-0 d-flex flex-column align-items-md-end">
                    {isApprovedAndFuture && (
                        <div className="mt-2 d-flex flex-column align-items-end">
                            <Button 
                            size="sm" 
                            variant="outline-info" 
                            className="mb-1 w-100 w-md-auto"
                            onClick={() => handleOpenReleaseModalAdmin(request.id)}
                            disabled={loadingRequests}
                        >
                            Release
                        </Button>
                        </div>
                    )}
                    {isPending && (
                        <div className="mt-2 d-flex flex-column align-items-end">
                            <Button 
                                variant="success" 
                                size="sm" 
                                className="mb-1 w-100 w-md-auto" 
                                onClick={() => handleStatusUpdate(request.id, 'APPROVED', storeUser.userName)}
                                disabled={loadingRequests}
                            >
                                <i className="bi bi-check-lg"></i> Approve
                            </Button>
                            <Button 
                                variant="danger" 
                                size="sm" 
                                className="w-100 w-md-auto"
                                onClick={() => handleStatusUpdate(request.id, 'REJECTED', storeUser.userName)}
                                disabled={loadingRequests}
                            >
                                <i className="bi bi-x-lg"></i> Reject
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    RequestItem.propTypes = {
        request: PropTypes.shape({
            id: PropTypes.number.isRequired,
            status: PropTypes.oneOf(['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED', 'RELEASED']).isRequired,
            bookingDate: PropTypes.instanceOf(Date).isRequired,
            site: PropTypes.string.isRequired,
            slotType: PropTypes.oneOf(Object.keys(SLOT_TYPES)).isRequired,
            requestedBy: PropTypes.string.isRequired,
            created: PropTypes.instanceOf(Date).isRequired,
            bayNo:  PropTypes.string,
            plateNo:  PropTypes.string,
        }).isRequired,
    };
    return (
        <div style={{ position: 'relative' }}>
            <Container className="p-4 p-md-5 my-4 bg-white shadow-lg rounded-3">
                
                {/* <ToastContainer position="top-right" autoClose={5000} hideProgressBar newestOnTop closeOnClick pauseOnHover theme="colored" /> */}

                {/* Confirm Release Modal */}
                <ConfirmModal 
                    show={showReleaseModalAdmin}
                    handleClose={() => setShowReleaseModalAdmin(false)}
                    handleConfirm={handleReleaseConfirmAdmin}
                    title="Confirm Charging Space Booking"
                    body="Are you sure you want to release this approved request? This action cannot be undone."
                    confirmText="Release"
                />
                
                {/* Modal User Settings */}
                <UserSettingsModal 
                    show={showSettingsModal} 
                    handleClose={() => setShowSettingsModal(false)}
                    allUsers={allUsers}
                    refreshUserList={loadUsers}
                />

                {/* Card 1: Header */}
                <PageHeader
                    title="EV Charging Space Booking Requests"
                    description={`Request result for charging space booking.`}
                    className="mb-3"
                ></PageHeader>
                {loadingRequests && (
                    <div className="text-white d-flex align-items-center small">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Loading Requests...
                    </div>
                )}

                {/* Card 2: Controls & Filters */}
                <Card className="mb-4 shadow-sm rounded-4 border-0">
                    <Card.Body>
                        <Row className="align-items-end g-3">
                            <Col lg={3} md={6} className="order-md-1 order-lg-1">
                                <Form.Group>
                                    <Form.Label className="fw-bold">Booking User:</Form.Label>
                                    <Form.Select 
                                        value={filterUser} 
                                        onChange={(e) => setFilterUser(e.target.value)} 
                                        disabled={loadingRequests || loadingUsers}
                                        className="shadow-sm"
                                    >
                                        <option value="all">-- Display all ({requestingUsersForFilter.length}) --</option>
                                        {requestingUsersForFilter
                                            .sort((a, b) => getUserName(a.userId).localeCompare(getUserName(b.userId))) 
                                            .map(user => (
                                                <option key={user.userId} value={user.userName}>
                                                    {user.userName}
                                                </option>
                                            ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col lg={3} md={6} className="order-md-2 order-lg-2">
                                <Form.Group>
                                    <Form.Label className="fw-bold">Charging Site:</Form.Label>
                                    <Form.Select 
                                        value={filterSite} 
                                        onChange={(e) => setFilterSite(e.target.value)} 
                                        disabled={loadingRequests}
                                        className="shadow-sm"
                                    >
                                        <option value="all">-- All Sites --</option>
                                        {SITES.map(s => (
                                            <option key={s.id} value={s.id}>
                                                {s.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            
                            {/* col 2 & 3: Date Range Filter */}
                            <Col lg={4} md={12} className="order-md-3 order-lg-2">
                                <Form.Label className="fw-bold">Booking Date Range:</Form.Label>
                                <InputGroup className="shadow-sm">
                                    <Form.Control
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                    />
                                    <InputGroup.Text className="bg-light fw-bold">to</InputGroup.Text>
                                    <Form.Control
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                    />
                                    <Button
                                        variant="primary"
                                        onClick={handleDateSearch}
                                        disabled={loadingRequests}
                                    >
                                        <i className="bi bi-search me-2"></i>
                                        Search
                                    </Button>
                                </InputGroup>
                            </Col>

                            {/* col 4: Search & Settings */}
                            <Col lg={2} md={12} className="order-md-2 order-lg-3">
                                <Form.Group>
                                    <Button
                                        variant="info"
                                        className="w-100 fw-bold shadow-sm text-white"
                                        onClick={() => setShowSettingsModal(true)}
                                        disabled={loadingUsers}
                                        >
                                        <i className="bi bi-gear me-2"></i>
                                        EV User Settings
                                    </Button>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Card 3: Requests Grouped by User (Accordion) */}
                <Card className="shadow-lg rounded-4 border-0">
                    <Card.Header as="h5" className="bg-light border-bottom rounded-top-4 p-3 d-flex align-items-center">
                        <i className="bi bi-list-check me-2 text-primary"></i>
                        Booking Requests 
                        <Badge bg="primary" className="ms-2 fs-6">{filteredRequestCount}</Badge>
                    </Card.Header>
                    <Card.Body className="p-0">
                        {loadingRequests ? (
                            <div className="text-center p-5 text-secondary">
                                <Spinner animation="border" className="mb-3" />
                                <p>Loading booking requests...</p>
                            </div>
                        ) : (
                            Object.keys(groupedRequests).length === 0 ? (
                                <Alert variant="info" className="m-3">
                                    <i className="bi bi-check-circle me-2"></i>
                                    There is no booking request found.
                                </Alert>
                            ) : (
                                <Accordion
                                    activeKey={activeKey}
                                    onSelect={(key) => {
                                        setActiveKey(prev => (prev === key ? null : key));
                                    }}
                                    alwaysOpen={false}
                                    >
                                    {Object.keys(groupedRequests).map(userName => (
                                        <Accordion.Item eventKey={userName} key={userName} className="border-top">
                                            <Accordion.Header>
                                                <span className="fw-bold me-3 text-dark">
                                                    <i className="bi bi-person-circle me-2 text-info"></i>
                                                    {getUserName(userName)}
                                                </span>
                                                <Badge bg="primary">
                                                    {groupedRequests[userName].length} Request
                                                </Badge>
                                                {
                                                    Array.isArray(groupedRequests[userName]) &&
                                                    groupedRequests[userName].filter(x => x.status === "PENDING").length > 0 && (
                                                        <Badge style={{ marginLeft: 10 }} bg="warning">
                                                        {groupedRequests[userName].filter(x => x.status === "PENDING").length} Pending
                                                        </Badge>
                                                    )
                                                }
                                            </Accordion.Header>
                                            <Accordion.Body className="p-3 bg-light">
                                               {groupedRequests[userName]?.map(req => (
                                                        <RequestItem
                                                            key={req.id}
                                                            request={{ 
                                                            ...req, 
                                                            plateNo: getPlateNo(userName) 
                                                            }}
                                                        />
                                                        ))}
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    ))}
                                </Accordion>
                            )
                        )}
                    </Card.Body>
                </Card>

                {/* Footer */}
                <footer className="text-center text-muted small pt-3 mt-4 border-top">
                    Display request from {format(defaultStartDate, 'd MMM')} to next 14 days.
                </footer>
            </Container>
        </div>
    );
}
